import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { CreateTenantSecretRequest, TenantSecretDto, GroupedTenantSecretsDto } from '../models/tenant-secret.model';

/**
 * Angular service for the /api/v1/tenant-secrets endpoints.
 *
 * All write operations POST plain request bodies; all read operations return
 * masked {@link TenantSecretDto} objects that never expose the raw client
 * secret.
 */
@Injectable({
  providedIn: 'root'
})
export class TenantSecretService {

  /** Absolute base URL for the tenant-secrets resource, derived from environment config. */
  private readonly baseUrl: string;

  constructor(private readonly http: HttpClient) {
    this.baseUrl = `${environment.apiUrl}/v1/tenant-secrets`;
  }

  /**
   * Retrieves every TenantSecret in the shared pool.
   *
   * @returns Observable of a masked {@link TenantSecretDto} array.
   */
  public getAll(): Observable<TenantSecretDto[]> {
    return this.http.get<TenantSecretDto[]>(this.baseUrl);
  }

  /**
   * Retrieves tenant secrets grouped by TenantId for hierarchical dropdown display.
   * Each group contains a tenant representative (with TenantName if resolved) and
   * all client secrets associated with that tenant.
   *
   * @returns Observable of {@link GroupedTenantSecretsDto} with tenants and their clients.
   */
  public getGrouped(): Observable<GroupedTenantSecretsDto> {
    return this.http.get<GroupedTenantSecretsDto>(`${this.baseUrl}/tenant-clients-grouped`);
  }

  /**
   * Retrieves a single TenantSecret by its ID.
   *
   * @param id - The MongoDB ObjectId string of the TenantSecret.
   * @returns Observable of the masked {@link TenantSecretDto}.
   */
  public getById(id: string): Observable<TenantSecretDto> {
    return this.http.get<TenantSecretDto>(`${this.baseUrl}/${id}`);
  }

  /**
   * Creates a new TenantSecret in the shared pool.
   *
   * @param request - Creation payload containing label, tenantId, clientId, and clientSecret.
   * @returns Observable of the created, masked {@link TenantSecretDto}.
   */
  public create(request: CreateTenantSecretRequest): Observable<TenantSecretDto> {
    return this.http.post<TenantSecretDto>(this.baseUrl, request);
  }
}
